import React, { useState, useEffect } from "react";
import { Upload, DefaultHttpStack } from "tus-js-client";

const VideoUploader = () => {
  const [file, setFile] = useState(null);
  const [title, setTitle] = useState("");
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [videoUrl, setVideoUrl] = useState(null);
  const [error, setError] = useState(null);
  const [categories, setCategories] = useState([]);
  const [selectedCategories, setSelectedCategories] = useState({});
  const [expandedCategories, setExpandedCategories] = useState({});
  const [loadingCategories, setLoadingCategories] = useState(false);
  const [uploadStatus, setUploadStatus] = useState("idle");

  useEffect(() => {
    const fetchCategories = async () => {
      setLoadingCategories(true);
      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/api/fetch-categories?taxonomy=Video`
        );
        const data = await response.json();
        setCategories(data.data.terms || []); // ✅ Only store the terms
      } catch (err) {
        setError("Failed to load categories");
      } finally {
        setLoadingCategories(false);
      }
    };
    fetchCategories();
  }, []);

  const handleFileChange = (e) => {
    setFile(e.target.files[0]);
    setVideoUrl(null);
    setError(null);
  };

  const handleTitleChange = (e) => {
    setTitle(e.target.value);
  };

  const toggleExpand = (catID) => {
    setExpandedCategories((prev) => ({
      ...prev,
      [catID]: !prev[catID],
    }));
  };

  const toggleCategorySelection = (categoryId) => {
    setSelectedCategories((prev) => ({
      ...prev,
      [categoryId]: !prev[categoryId],
    }));
  };

  const handleUpload = async () => {
    if (!file) {
      setError("Please select a video file.");
      return;
    }

    if (!title) {
      setError("Please enter a title for the video.");
      return;
    }

    const selectedCategoryIds = Object.keys(selectedCategories).filter(
      (id) => selectedCategories[id]
    );
    if (selectedCategoryIds.length === 0) {
      setError("Please select at least one category.");
      return;
    }

    try {
      setUploadStatus("initiating");
      const initResponse = await fetch(
        `${process.env.REACT_APP_API_URL}/api/upload-video`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            title,
            file_size: file.size,
            categories: selectedCategoryIds,
          }),
        }
      );

      const initData = await initResponse.json();
      if (!initResponse.ok)
        throw new Error(initData.message || "Failed to initiate upload");

      setUploadStatus("uploading");
      setUploading(true);
      setProgress(0);

      const upload = new Upload(file, {
        endpoint: initData.data.upload_link,
        uploadUrl: initData.data.upload_link,
        retryDelays: [0, 3000, 5000, 10000],
        chunkSize: 5 * 1024 * 1024,
        metadata: {
          filename: btoa(unescape(encodeURIComponent(file.name))),
          filetype: btoa(unescape(encodeURIComponent(file.type))),
        },
        headers: {},
        httpStack: new DefaultHttpStack(),
        onError: (error) => {
          console.error("Upload failed:", error);
          setError(`Upload failed: ${error.message}`);
          setUploadStatus("error");
          setUploading(false);
        },
        onProgress: (bytesUploaded, bytesTotal) => {
          setProgress((bytesUploaded / bytesTotal) * 100);
        },
        onSuccess: async () => {
          try {
            setUploadStatus("finalizing");
            const finalizeResponse = await fetch(
              `${process.env.REACT_APP_API_URL}/api/finalize-upload/${initData.data.vimeo_id}`,
              {
                method: "POST",
                headers: {
                  "Content-Type": "application/json",
                },
                body: JSON.stringify({
                  title,
                  original_name: file.name,
                  categories: selectedCategoryIds,
                }),
              }
            );

            const finalData = await finalizeResponse.json();
            if (!finalizeResponse.ok)
              throw new Error(finalData.message || "Finalization failed");

            setVideoUrl(finalData.data.embed_url);
            setUploadStatus("completed");
          } catch (err) {
            setError(
              `Upload completed but finalization failed: ${err.message}`
            );
            setUploadStatus("error");
          } finally {
            setUploading(false);
          }
        },
      });

      upload.options.method = "PATCH";
      upload.start();
    } catch (err) {
      console.error("Upload process error:", err);
      setError(err.message);
      setUploadStatus("error");
      setUploading(false);
    }
  };

  const renderCategoryTree = (category, level = 0) => {
    const hasChildren = category.children && category.children.length > 0;
    const isExpanded = expandedCategories[category.id];
    const isSelectable = category.taxonomy !== null;

    return (
      <div
        key={`cat-${category.id}`}
        className="mb-2"
        style={{ marginLeft: `${level * 16}px` }}
      >
        <div className="d-flex align-items-center">
          {category.taxonomy !== null ? (
            <>
              <input
                type="checkbox"
                id={`cat-${category.id}`}
                checked={!!selectedCategories[category.id]}
                onChange={() => toggleCategorySelection(category.id)}
                className="form-check-input me-2"
              />
              <label
                htmlFor={`cat-${category.id}`}
                className="form-check-label"
                style={{ cursor: "pointer" }}
              >
                {category.name}
              </label>
            </>
          ) : (
            <div
              className="d-flex align-items-center"
              style={{ cursor: hasChildren ? "pointer" : "default" }}
              onClick={() => hasChildren && toggleExpand(category.id)}
            >
              {hasChildren && (
                <span className="me-2">{isExpanded ? "−" : "+"}</span>
              )}
              <span>{category.name}</span>
            </div>
          )}
        </div>

        {isExpanded &&
          hasChildren &&
          category.children.map((child) =>
            renderCategoryTree(child, level + 1)
          )}
      </div>
    );
  };

  const getButtonText = () => {
    switch (uploadStatus) {
      case "initiating":
        return "Preparing upload...";
      case "uploading":
        return `Uploading... ${Math.round(progress)}%`;
      case "finalizing":
        return "Finalizing...";
      case "completed":
        return "Upload Complete";
      case "error":
        return "Try Again";
      default:
        return "Publish";
    }
  };

  return (
    <div className="container">
      <h3 className="mb-4">Add a Lesson</h3>

      <div className="row">
        <div className="col-md-8">
          <div className="mb-3">
            <input
              type="text"
              className="form-control mb-2"
              placeholder="Enter video title"
              value={title}
              onChange={handleTitleChange}
            />
            <input
              type="file"
              accept="video/mp4,video/quicktime"
              className="form-control"
              onChange={handleFileChange}
            />
          </div>

          {file && (
            <div className="mb-3">
              <small className="text-muted">
                Selected: {file.name} ({Math.round(file.size / (1024 * 1024))}{" "}
                MB)
              </small>
            </div>
          )}

          <button
            className="btn btn-inchworm"
            onClick={handleUpload}
            disabled={
              uploading ||
              uploadStatus === "initiating" ||
              uploadStatus === "verifying"
            }
          >
            {getButtonText()}
          </button>

          {uploadStatus === "uploading" && (
            <div className="progress mt-3">
              <div
                className="progress-bar progress-bar-striped progress-bar-animated"
                role="progressbar"
                style={{ width: `${progress}%` }}
                aria-valuenow={progress}
                aria-valuemin="0"
                aria-valuemax="100"
              >
                {Math.round(progress)}%
              </div>
            </div>
          )}

          {error && (
            <div className="alert alert-danger mt-3" role="alert">
              {error}
            </div>
          )}

          {uploadStatus === "completed" && videoUrl && (
            <div className="alert alert-success mt-3" role="alert">
              Video uploaded successfully!
              <br />
              <a href={videoUrl} target="_blank" rel="noopener noreferrer">
                View on Vimeo
              </a>
            </div>
          )}
        </div>

        <div className="col-md-4">
          <div className="card">
            <div className="card-header bg-white">
              <h5 className="mb-0">Categories</h5>
            </div>
            <div
              className="card-body"
              style={{ maxHeight: "400px", overflowY: "auto" }}
            >
              {loadingCategories ? (
                <p>Loading categories...</p>
              ) : (
                <div className="category-tree">
                  {categories.map((category) => renderCategoryTree(category))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VideoUploader;
